import { useCallback, useEffect, useState } from 'react'
import { NavLink, useMatch } from 'react-router-dom'
import {
  listMembers, setMemberRole, removeMember, revokeMemberSessions,
  type AuthenticatedSession, type OrgMember,
} from '../../api'
import { dateTime } from '../../format'
import { AdminPage } from './AdminPage'
import { ConfirmSheet } from '../../components/ConfirmSheet'
import { InviteList } from './Invitations'

/**
 * Who is in the organization, and the three things an administrator can do to
 * them.
 *
 * The three are deliberately distinct, because they mean different things and
 * two of them are routinely confused:
 *
 * - **Change role** — what they may do here from their next request onward.
 *   Nothing is rotated: privilege lives in the membership row and is re-read
 *   every time, so the change lands with the token they already hold.
 * - **Sign out everywhere** — ends their sessions and leaves the membership.
 *   For a lost laptop.
 * - **Remove** — ends the membership. Their access to this organization stops,
 *   and the signing links other organizations sent them are untouched, because
 *   a membership ending is not a credential changing.
 *
 * The last administrator cannot be demoted or removed. The store refuses it;
 * this surfaces the refusal rather than predicting it, so the rule has one
 * home.
 */
export function Members({ session }: { session: AuthenticatedSession }) {
  const invitations = useMatch('/orgs/:orgId/members/invitations')
  return (
    <AdminPage
      session={session}
      title="Members"
      // True in every deployment. "Only an invited address can sign in" was
      // false where self-serve signup is on (DOCSDONE_SELF_SERVE_SIGNUP):
      // anybody can sign in there, and opens an organization of their own.
      // "Only an invited address can join" was false once company sign-in is
      // on: a login through the organization's identity provider makes the
      // person a member with no invitation (federated.go,
      // ensureBrokeredMembershipTx), so an administrator who sent none
      // believed the organization was closed to the rest of the domain.
      intro={invitations
        ? 'Invited addresses can join this organization. With company sign-in on, staff at its domains also join when they sign in with your identity provider.'
        : 'Who can send documents from this organization, and what each of them may do.'}
    >
      {(orgId) => (
        <>
          <nav className="filter-tabs" aria-label="Member lists">
            <NavLink
              to={`/orgs/${orgId}/members`}
              end
              className={({ isActive }) => `filter-tab${isActive ? ' is-active' : ''}`}
            >
              Members
            </NavLink>
            <NavLink
              to={`/orgs/${orgId}/members/invitations`}
              className={({ isActive }) => `filter-tab${isActive ? ' is-active' : ''}`}
            >
              Invitations
            </NavLink>
          </nav>
          {invitations
            ? <InviteList key={orgId} orgId={orgId} />
            : <MembersTable key={orgId} orgId={orgId} session={session} />}
        </>
      )}
    </AdminPage>
  )
}

type Pending = { kind: 'remove' | 'revoke'; member: OrgMember }

function MembersTable({ orgId, session }: { orgId: string; session: AuthenticatedSession }) {
  const [members, setMembers] = useState<OrgMember[] | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState('')
  const [pending, setPending] = useState<Pending | null>(null)
  const [sheetError, setSheetError] = useState('')

  const load = useCallback(() => {
    listMembers(orgId)
      .then((r) => setMembers(r.members ?? []))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not read the roster.'))
  }, [orgId])

  // Only the load. Resetting to "loading" on an organization change is done by
  // remounting — the caller keys this component on the organization id — which
  // is what keeps one organization's roster from flashing under another's
  // heading without a cascading render to undo it.
  useEffect(load, [load])

  async function changeRole(m: OrgMember, role: 'admin' | 'member') {
    setBusy(m.party_id)
    setError('')
    setNotice('')
    try {
      await setMemberRole(orgId, m.party_id, role)
      setNotice(`${m.display_name} is now ${role === 'admin' ? 'an administrator' : 'a member'}.`)
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change that role.')
    } finally {
      setBusy('')
    }
  }

  async function confirmPending() {
    if (!pending) return
    setBusy(pending.member.party_id)
    setSheetError('')
    try {
      if (pending.kind === 'remove') {
        await removeMember(orgId, pending.member.party_id)
        setNotice(`${pending.member.display_name} is no longer a member.`)
      } else {
        await revokeMemberSessions(orgId, pending.member.party_id)
        setNotice(`${pending.member.display_name} has been signed out everywhere.`)
      }
      setPending(null)
      load()
    } catch (e) {
      setSheetError(e instanceof Error ? e.message : 'That did not work.')
    } finally {
      setBusy('')
    }
  }

  if (error && members === null) {
    return <div className="notice notice-bad">{error}</div>
  }
  if (members === null) return <p className="muted">Loading…</p>

  const admins = members.filter((m) => m.role === 'admin').length

  return (
    <>
      {error && <div className="notice notice-bad" style={{ marginBottom: '.9rem' }}>{error}</div>}
      {notice && <div className="notice notice-good" style={{ marginBottom: '.9rem' }}>{notice}</div>}

      <div className="card card-tight table-wrap">
        <table className="table table-rows">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              <th>Joined</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const isYou = m.party_id === session.identity.party_id
              // The store refuses to leave an organization with no
              // administrator. Saying so before the click is kinder than a
              // refusal after it, and the store is still the one that decides.
              const lastAdmin = m.role === 'admin' && admins === 1
              return (
                <tr key={m.party_id}>
                  <td>
                    <strong>{m.display_name}</strong>
                    {isYou && <span className="tag" style={{ marginLeft: '.4rem' }}>you</span>}
                    {m.kind === 'service' && (
                      <div className="faint small">Service account</div>
                    )}
                  </td>
                  <td>
                    <select
                      aria-label={`Role for ${m.display_name}`}
                      value={m.role}
                      disabled={busy === m.party_id || lastAdmin}
                      onChange={(e) => void changeRole(m, e.target.value as 'admin' | 'member')}
                    >
                      <option value="admin">Administrator</option>
                      <option value="member">Member</option>
                    </select>
                    {lastAdmin && (
                      <div className="faint small">Last administrator</div>
                    )}
                  </td>
                  <td className="muted small">{dateTime(m.created_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="row-tight" style={{ justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-secondary btn-small"
                        aria-label={`Sign ${m.display_name} out everywhere`}
                        disabled={busy === m.party_id}
                        onClick={() => { setSheetError(''); setPending({ kind: 'revoke', member: m }) }}
                      >
                        Sign out everywhere
                      </button>
                      <button
                        className="btn btn-secondary btn-small"
                        aria-label={`Remove ${m.display_name}`}
                        disabled={busy === m.party_id || lastAdmin}
                        onClick={() => { setSheetError(''); setPending({ kind: 'remove', member: m }) }}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {pending?.kind === 'revoke' && (
        <ConfirmSheet
          title={`Sign ${pending.member.display_name} out everywhere?`}
          confirmLabel="Sign them out"
          busy={busy !== ''}
          error={sheetError}
          onConfirm={() => void confirmPending()}
          onClose={() => setPending(null)}
        >
          <SignOutWords />
        </ConfirmSheet>
      )}

      {pending?.kind === 'remove' && (
        <ConfirmSheet
          title={`Remove ${pending.member.display_name}?`}
          confirmLabel="Remove them"
          busy={busy !== ''}
          error={sheetError}
          onConfirm={() => void confirmPending()}
          onClose={() => setPending(null)}
        >
          <RemoveWords />
        </ConfirmSheet>
      )}
    </>
  )
}

/**
 * What signing a member out everywhere does. It also ends the chats they
 * connected to this organization, and only those: a chat names its
 * organization, and one they connected elsewhere belongs to that
 * organization (94a72bf). The sheet said nothing about chats.
 */
export function SignOutWords() {
  return (
    <p className="small muted">
      Their sessions end, and chats they connected to this organization are disconnected. They stay a member and can
      sign in again.
    </p>
  )
}

/** What removing a member does, chats included, as SignOutWords says. */
export function RemoveWords() {
  return (
    <>
      <p className="small muted">
        They lose access to this organization's documents, and their sessions end. Chats they connected to this
        organization are disconnected.
      </p>
      <p className="small muted">
        Documents they signed are unchanged. Signing links other organizations sent them still work.
      </p>
    </>
  )
}
